import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@qodo/design-system';
import { type PricingConfig } from 'hooks/billing/usePricingConfig';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { mixpanelService } from 'services/mixpanel';
import {
  MIXPANEL_EVENT_NAMES,
  MIXPANEL_OPERATIONS,
} from 'services/mixpanel/consts';
import { spawn } from 'utils/translations';
import { SkuCard } from './SkuCard';

const spawnPopup = spawn('billing.popup');

// Dialog surface — matches the DS dark dialog background (#141414). Used for
// the scroll-edge gradient so cards fade into the panel rather than hard-clip.
const DIALOG_BG = 'var(--color-purple-muted-800)';

// One card width (w-44 = 176px) + gap (gap-3 = 12px).
const SCROLL_STEP = 188;

interface SkuSelectorProps {
  cfg: PricingConfig;
  activeSku: string | undefined;
  /** Usage-recommended SKU (PLT-4059) — gets the "Recommended" badge. */
  recommendedSku?: string;
  /** The user's current plan SKU — rendered as the marked, non-selectable card. */
  currentPlanSku?: string;
  onSelectSku: (sku: string) => void;
}

/**
 * Horizontally-scrollable row of SKU cards (an accessible radiogroup). Gradient
 * shadows + arrow buttons appear on whichever side(s) have hidden content.
 */
export const SkuSelector = ({
  cfg,
  activeSku,
  recommendedSku,
  currentPlanSku,
  onSelectSku,
}: SkuSelectorProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;

    if (!el) return;

    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;

    if (!el) return;

    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);

    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  useEffect(() => {
    updateScrollState();
  }, [cfg.plans, updateScrollState]);

  const trackCarouselClick = (direction: 'left' | 'right') => {
    mixpanelService.track(MIXPANEL_EVENT_NAMES.ACTION, {
      operation: MIXPANEL_OPERATIONS.PLAN_CAROUSEL_CLICKED,
      interaction_details: { carousel_direction: direction },
    });
  };

  const onScrollLeft = () => {
    trackCarouselClick('left');
    scrollRef.current?.scrollBy({ left: -SCROLL_STEP, behavior: 'smooth' });
  };

  const onScrollRight = () => {
    trackCarouselClick('right');
    scrollRef.current?.scrollBy({ left: SCROLL_STEP, behavior: 'smooth' });
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div className="relative">
        {canScrollLeft && (
          <>
            <div
              className="pointer-events-none absolute inset-y-0 left-0 z-10 w-32"
              style={{
                background: `linear-gradient(to right, ${DIALOG_BG}, transparent)`,
              }}
            />
            <Button
              size="icon-md"
              variant="outline"
              aria-label={spawnPopup('sku.scroll-left-aria')}
              onClick={onScrollLeft}
              className="absolute left-2 top-1/2 z-20 -translate-y-1/2"
              icon={ChevronLeft}
            />
          </>
        )}

        {canScrollRight && (
          <>
            <div
              className="pointer-events-none absolute inset-y-0 right-0 z-10 w-32"
              style={{
                background: `linear-gradient(to left, ${DIALOG_BG}, transparent)`,
              }}
            />
            <Button
              size="icon-md"
              variant="outline"
              aria-label={spawnPopup('sku.scroll-right-aria')}
              onClick={onScrollRight}
              className="absolute right-2 top-1/2 z-20 -translate-y-1/2"
              icon={ChevronRight}
            />
          </>
        )}

        <div
          ref={scrollRef}
          role="radiogroup"
          aria-label={spawnPopup('sku-aria-label')}
          className="flex gap-3 overflow-x-auto pb-1 pt-3 [scrollbar-color:var(--border-interactive-neutral-idle)_transparent] [scrollbar-width:thin]"
        >
          {cfg.plans.map((plan) => (
            <SkuCard
              key={plan.sku}
              plan={plan}
              cfg={cfg}
              checked={activeSku === plan.sku}
              recommended={
                recommendedSku != null && recommendedSku === plan.sku
              }
              isCurrentPlan={currentPlanSku === plan.sku}
              onSelect={onSelectSku}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
